/**
 * @Description 商圈详情-基本信息栏
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { fixNumber } from '@/common/utils/ways';
import HeatMap from '@/common/components/business/PopulationHeatMap/HeatMap';
import { showMarker } from '@/common/utils/map';
import { industryCodeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import { BisConType, PopulationData } from './ts-config';
import Group from './Group';
import GroupHorizontal from './GroupHorizontal';
import BlockHorizontal from './BlockHorizontal';


const selectedOptions = {
  strokeColor: '#FF861D',
  fillColor: '#FF861D',
  fillOpacity: 0.2,
  strokeWeight: 0.5
};
const defaultOptions = {
  strokeColor: '#4792FD',
  strokeWeight: 0.5, // isCustom ? 10 : 2,
  strokeOpacity: 1,
  fillOpacity: 0,
  strokeDasharray: [10, 10],
  // fillColor: '#006AFF',
};
const Basic: FC<any> = ({
  overView,
  detail,
  mapIns,
  rangeMarkerRef,
  setIsOpenHeatMap,
  isShare,
  shareParams = {},
}) => {
  const [active, setActive] = useState<any>({
    keyName: '',
    radius: 0,
    basicParams: {}
  });// 选中的热力

  const populationStr = (count) => {
    if (!isNotEmpty(count)) return '-';
    // return +count >= 10000
    //   ? `${beautifyThePrice(count / 10000.0, ',', 1)}`
    //   : beautifyThePrice(Math.floor(count), ',', 0);
    return +count >= 10000
      ? `${fixNumber(count / 10000, 1)}`
      : Math.floor(count);
  };

  const targetUnit = (count) => {
    if (!isNotEmpty(count)) return '';
    return +count >= 10000 ? '万人' : '人';
  };

  const populationData = useMemo((): PopulationData => {
    const population500m: BisConType = {
      desc: `周边500m居住人口`,
      title: `500m`,
      value: populationStr(overView?.population),
      unit: targetUnit(overView?.population),
      basicValue: overView?.population,
      tag: overView?.population500mRank,
      isHeatMap: true,
      radius: 500,
      keyName: 'population',
      basicParams: {
        queryType: 1,
      }
    };
    const population1500m: BisConType = {
      desc: `周边1.5km居住人口`,
      title: `1.5km`,
      value: populationStr(overView?.population1500m),
      unit: targetUnit(overView?.population1500m),
      basicValue: overView?.population1500m,
      tag: overView?.population1500mRank,
      isHeatMap: true,
      keyName: 'population1500m',
      radius: 1500,
      basicParams: {
        queryType: 1,
      }
    };
    const population3000m: BisConType = {
      desc: `周边3km居住人口`,
      title: `3km`,
      value: populationStr(overView?.population3km),
      basicValue: overView?.population3km,
      unit: targetUnit(overView?.population3km),
      tag: overView?.peopleRank,
      isHeatMap: true,
      radius: 3000,
      keyName: 'population3km',
      basicParams: {
        queryType: 1,
      }
    };
    const populationWork500m: BisConType = {
      desc: `周边500m工作人口`,
      title: `500m`,
      value: populationStr(overView?.populationWork500m),
      unit: targetUnit(overView?.populationWork500m),
      basicValue: overView?.populationWork500m,
      tag: overView?.populationWork500mRank,
      isHeatMap: true,
      radius: 500,
      keyName: 'populationWork500m',
      basicParams: {
        queryType: 2,
      }
    };
    const populationWork1500m: BisConType = {
      desc: `周边1.5km工作人口`,
      title: `1.5km`,
      value: populationStr(overView?.populationWork1500m),
      basicValue: overView?.populationWork1500m,
      unit: targetUnit(overView?.populationWork1500m),
      tag: overView?.populationWork1500mRank,
      isHeatMap: true,
      keyName: 'populationWork1500m',
      radius: 1500,
      basicParams: {
        queryType: 2,
      }
    };
    const populationWork3000m: BisConType = {
      desc: `周边3km工作人口`,
      title: `3km`,
      value: populationStr(overView?.populationWork3000m),
      basicValue: overView?.populationWork3000m,
      unit: targetUnit(overView?.populationWork3000m),
      tag: overView?.populationWork3000mRank,
      isHeatMap: true,
      keyName: 'populationWork3000m',
      radius: 3000,
      basicParams: {
        queryType: 2,
      }
    };
    const residentPopulation500m: BisConType = {
      desc: `周边500m常住人口`,
      title: `500m`,
      value: populationStr(overView?.residentPopulation500m),
      unit: targetUnit(overView?.residentPopulation500m),
      basicValue: overView?.residentPopulation500m,
      tag: overView?.residentPopulation500mRank,
      isHeatMap: true,
      keyName: 'residentPopulation500m',
      radius: 500,
      basicParams: {
        queryType: 3,
      }
    };
    const residentPopulation1500m: BisConType = {
      desc: `周边1.5km常住人口`,
      title: `1.5km`,
      value: populationStr(overView?.residentPopulation1500m),
      basicValue: overView?.residentPopulation1500m,
      unit: targetUnit(overView?.residentPopulation1500m),
      tag: overView?.residentPopulation1500mRank,
      isHeatMap: true,
      keyName: 'residentPopulation1500m',
      radius: 1500,
      basicParams: {
        queryType: 3,
      }
    };
    const residentPopulation3000m: BisConType = {
      desc: `周边3km常住人口`,
      title: `3km`,
      value: populationStr(overView?.residentPopulation3000m),
      unit: targetUnit(overView?.residentPopulation3000m),
      basicValue: overView?.residentPopulation3000m,
      tag: overView?.residentPopulation3000mRank,
      isHeatMap: true,
      keyName: 'residentPopulation3000m',
      radius: 3000,
      basicParams: {
        queryType: 3,
      }
    };

    const showMallFlow = !!overView?.mallPassFlow;
    const passengerFlow: any = showMallFlow ? {
      desc: '商场日均客流',
      title: '商场日均客流',
      value: populationStr(overView?.mallPassFlow),
      unit: targetUnit(overView?.mallPassFlow),
      basicValue: overView?.mallPassFlow,
      tag: overView?.flowRank,
      keyName: 'mallPassFlow',
    } : null;

    const { industryCode } = overView || {};
    // 判断属于汽车行业且有值，才展示有车比例
    const showCarRate = [industryCodeEnum.carSale, industryCodeEnum.carRepair].includes(industryCode) && !!overView?.carRate;
    const carRate: any = showCarRate ? {
      desc: '有车客群比例',
      title: '有车客群比例',
      value: overView?.carRate ? overView?.carRate + '%' : '-',
      basicValue: overView?.carRate,
      tag: overView?.carRateRank,
      keyName: 'carRate',
    } : null;

    const res: PopulationData = {
      carRate: carRate,
      passengerFlow: passengerFlow,
      population: [
        population500m,
        population1500m,
        population3000m,
      ],
      populationWork: [
        populationWork500m,
        populationWork1500m,
        populationWork3000m,
      ],
      residentPopulation: [
        residentPopulation500m,
        residentPopulation1500m,
        residentPopulation3000m,
      ],
    };
    return res;
  }, [overView]);

  /**
   * @description 不展示汽车保有率、与商场客流数据
   */
  const isNoCarNoPassenger = useMemo(() => {
    return populationData && !isNotEmptyAny(populationData.carRate) && !isNotEmptyAny(populationData.passengerFlow);
  }, [populationData]);

  /**
   * @description 展示汽车保有率、或商场客流数据中的至少一个
   */
  const isHasCarOrMall = useMemo(() => {
    return populationData && (isNotEmptyAny(populationData.carRate) || isNotEmptyAny(populationData.passengerFlow));
  }, [populationData]);

  const onHeatEvent = (checked: boolean, item: BisConType) => {
    // 如果是取消，或者数据不存在则取消
    if (!checked || !(item.isHeatMap/* && item?.basicValue*/)) {
      setActive({
        keyName: '',
        basicParams: {},
        radius: 0
      });
      setIsOpenHeatMap(false);
      return;
    }
    setIsOpenHeatMap(true);
    setActive({
      keyName: item.keyName,
      radius: item.radius,
      basicParams: item.basicParams,
    });
  };

  const handleSelectMarker = (radius) => {
    const { markerOf500, markerOf1500, markerOf3000 } = rangeMarkerRef.current || {};
    if (!markerOf500 || !markerOf1500 || !markerOf3000) return;
    markerOf500.circleMarker.setOptions(defaultOptions);
    handleContentMarker(500, markerOf500.contentMarker);
    markerOf1500.circleMarker.setOptions(defaultOptions);
    handleContentMarker(1500, markerOf1500.contentMarker);
    markerOf3000.circleMarker.setOptions(defaultOptions);
    handleContentMarker(3000, markerOf3000.contentMarker);

    if (radius === 500) {
      const { circleMarker, contentMarker } = markerOf500;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(500, contentMarker, true);
    }
    if (radius === 1500) {
      const { circleMarker, contentMarker } = markerOf1500;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(1500, contentMarker, true);
    }
    if (radius === 3000) {
      const { circleMarker, contentMarker } = markerOf3000;
      mapIns.setFitView(circleMarker, false, [0, 0, 0, 0]);
      circleMarker.setOptions(selectedOptions);
      handleContentMarker(3000, contentMarker, true);
    }
  };
  const handleContentMarker = (radius, marker, isSelected = false) => {
    let content = '';
    switch (radius) {
      case 500:
        content = '500m(5分钟步行圈)';
        break;
      case 1500:
        content = '1.5km(15分钟生活圈)';
        break;
      case 3000:
        content = '3km(外卖覆盖)';
        break;
    }
    showMarker(marker, <div className={isSelected ? styles.distanceHintOfSelected : styles.distanceHint}>
      {content}
    </div>);
  };

  useEffect(() => {
    handleSelectMarker(active?.radius);
  }, [active.radius]);

  const renderNoCarNoMall = () => {
    return (
      <Row gutter={12}>
        <Col span={8}>
          <Group groupName='居住人口' list={populationData?.population} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={8}>
          <Group groupName='办公人口' list={populationData?.populationWork} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={8}>
          <Group groupName='常住人口' list={populationData?.residentPopulation} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
      </Row>
    );
  };

  const renderHasCarOrMall = () => {
    return (
      <Row gutter={[8, 8]}>
        <Col span={12}>
          {
            isNotEmptyAny(populationData.passengerFlow) && isNotEmptyAny(populationData.carRate) ? <Row gutter={8}>
              <Col span={12}>
                <BlockHorizontal data={populationData.passengerFlow}/>
              </Col>
              <Col span={12}>
                <BlockHorizontal data={populationData.carRate}/>
              </Col>
            </Row> : isNotEmptyAny(populationData.passengerFlow) ? <BlockHorizontal data={populationData.passengerFlow} iconSrc={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_population_flow.png`}/> : isNotEmptyAny(populationData.carRate) ? <BlockHorizontal data={populationData.carRate} iconSrc={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_population_car.png`}/> : <></>
          }
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='居住人口' list={populationData?.population} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='办公人口' list={populationData?.populationWork} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
        <Col span={12}>
          <GroupHorizontal groupName='常住人口' list={populationData?.residentPopulation} active={active} onHeatEvent={onHeatEvent}/>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <div className={styles.basicContainer}>
        {isNoCarNoPassenger ? renderNoCarNoMall() : <></>}
        {isHasCarOrMall ? renderHasCarOrMall() : <></>}
      </div>
      <HeatMap
        mapIns={mapIns}
        active={active}
        detail={detail}
        isShare={isShare}
        shareParams={shareParams}
      />
    </>
  );
};

export default Basic;
